<template>
  <v-flex d-flex>
    <v-container fluid>
      <v-fab-transition>
        <v-btn v-show="toolbarReady" fixed bottom right dark fab color="pink" @click="refresh" :loading="loading">
          <v-icon>cached</v-icon>
        </v-btn>
      </v-fab-transition>
      <div class="elevation-1">
        <v-toolbar tabs color="grey lighten-5" style="position: relative">
          <v-toolbar-title class="px-2">{{$route.meta.title}}</v-toolbar-title>
          <v-spacer></v-spacer>
          <div v-if="permissions.logSearch" class="d-inline-block pt-2 mr-2">
            <LogSearch></LogSearch>
          </div>
          <div class="d-inline-block">
            <v-autocomplete
              v-model="model.account_id"
              label="Account"
              hide-details
              :items="accounts"
              item-text="key"
              item-value="value"
              :search-input.sync="accountSearch"
              :disabled="datatable.loading"
              prepend-icon="mdi-database"
            ></v-autocomplete>
          </div>
          <v-fade-transition>
            <v-progress-linear v-if="datatable.loading" indeterminate height="2" class="ma-0" style="position: absolute; bottom: 0px; left: 0; right: 0;"></v-progress-linear>
          </v-fade-transition>
        </v-toolbar>
        <div v-if="(!datatable.ready && datatable.loading)" class="elevation-1 white text-xs-center pa-3" style="transform: translateY(0px);">
          <span v-if="datatable.loading">{{ LOADING_TEXT }}</span>
          <span v-else>{{ NO_DATA_TEXT }}</span>
        </div>
        <template v-else>
          <BotMonitoringChart
            ref="chart"
            :data="data"
            :accountId="modelAccountId"
            :type="modelBotType"
            :range="model.dataRange"
            @change="data => onChartChanged(data)"
            @click="data => onChartClicked(data)"
          >
            <div v-if="botConfigTags.length > 0" slot="header" class="white">
              <div class="row d-flex align-center pb-3">
                <div class="flexbox align-center">
                  <div class="d-inline-block pr-1" style="width: 85px">Data Range:</div>
                  <div class="d-inline-block" style="width: 160px">
                    <v-select
                      v-model="model.dataRange"
                      :items="[1, 2, 3, 4, 5, 6, 7].reverse().map(x => ({ text: `Last ${x} day${x > 1 ? 's' : ''}`, value: x }))"
                      label="Days"
                      item-text="text"
                      item-value="value"
                      solo
                    ></v-select>
                  </div>
                </div>
                <div class="text-xs-right">
                  <v-chip v-for="tag in botConfigTags" :key="tag.key" small :color="tag.value ? 'primary' : 'grey'" text-color="white">
                    <v-avatar>
                      <v-icon color="white">{{ tag.value ? 'check_circle' : 'cancel' }}</v-icon>
                    </v-avatar>
                    {{ tag.key }}
                  </v-chip>
                </div>
              </div>
            </div>
          </BotMonitoringChart>
          <div style="position: relative">
            <v-tabs v-if="displayItems.length" v-model="tab" ref="monitorTabs" show-arrows >
              <v-tab v-for="item in displayItems" :key="item.key" ripple >
                {{ item.key }}
              </v-tab>
              <v-tab-item v-for="item in displayItems" :key="item.key" >
                <v-card flat>
                  <v-card-text>
                    <MonitorTable
                      ref="monitorDataTable"
                      :title="item.key"
                      :items="item.value"
                      :ready="datatable.ready"
                      :selectedId="datatable.selectedId"
                      @click:row="onClickRow"
                    />
                  </v-card-text>
                </v-card>
              </v-tab-item>
            </v-tabs>
            <div v-if="datatableTotalItems" class="text-xs-center">
              <MongoDataTableFooter
                :pagination.sync="datatable.pagination"
                :rowsPerPageItems="null"
              />
            </div>
            <div v-if="filteredChartLoading" class="d-flex align-center justify-center" style="position:absolute; left: 0; right: 0; top: 0; bottom: 0; background-color: rgba(255, 255, 255, .75)">
              <v-progress-circular :size="36" indeterminate color="primary" />
            </div>
          </div>
        </template>
      </div>
    </v-container>
  </v-flex>
</template>

<script>
import UserService from '../../services/user.service.js'
import MongoConnector from '../../services/mongo-connector.service'
import { getDefaultConnectorSchema } from '../../configs/schemas'
import MongoDataTable from '../../components/mongo-datatable/MongoDataTable'
import MongoDataTableFooter from '../../components/mongo-datatable/MongoDataTableFooter'
import MonitorTable from './MonitorTable'
import * as CloudWatch from '../../services/cloudwatch.service'
import LogSearch from './LogSearch'
import BotMonitoringChart from './BotMonitoringChart'
import Alert from '../../libs/alert'
import { groupArrayByObjectKey, objectToArray, isArray, debounce, sortByString, mergeObjectKeyFromArray, getProp, findIndex, isDefined, isBoolean, titleText } from '../../libs/utils'
import { FIELD_ID, LOADING_TEXT, NO_DATA_TEXT } from '../../libs/mongo-datatable.js'

export default {
  props: {
    account: { type: String, required: false }
  },
  data () {
    return {
      LOADING_TEXT,
      NO_DATA_TEXT,
      permissions: {
        logSearch: UserService.hasPermission('cloudwatch', 'search')
      },
      accountService: new MongoConnector(getDefaultConnectorSchema(null, 'accounts')),
      botConfigsService: new MongoConnector(getDefaultConnectorSchema(null, 'bot-configs')),
      dataService: new MongoConnector(getDefaultConnectorSchema(null, 'historical-executions')),
      tab: null,
      accountSearch: undefined,
      accounts: [],
      model: {
        dataRange: 3,
        account_id: null,
        botConfig: null
      },
      botConfigTags: [],
      data: [],
      chartFilter: {
        min: null,
        max: null
      },
      datatable: {
        ready: false,
        loading: false,
        selectedId: null,
        pagination: {
          page: 1,
          rowsPerPage: 50,
          totalItems: 0
        },
        currentGrouping: {
          from: +new Date(),
          to: +new Date()
        }
      },
      filteredChartLoading: false,
      filteredChartData: [],
      getFilteredChartData: debounce(() => {
        let data
        if (this.chartFilter.min && this.chartFilter.max) {
          data = this.data.filter(
            item => this.chartFilter.min <= item.created_at && item.created_at <= this.chartFilter.max
          )
        } else {
          data = this.data
        }
        this.filteredChartData = data
        this.filteredChartLoading = false
        this.$nextTick(() => {
          if (this.tab === null) {
            this.tab = '0'
          }
        })
      }, 500)
    }
  },
  computed: {
    modelAccountId () {
      return this.model.account_id
    },
    modelBotType () {
      const botType = getProp(this.model.botConfig || {}, 'type')
      return botType === undefined ? undefined : `${botType}`
    },
    toolbarReady () {
      return this.datatable.ready
    },
    loading () {
      return this.datatable.loading
    },
    pagingData () {
      const { page, rowsPerPage } = this.datatable.pagination
      const pageStartIndex = (page - 1) * rowsPerPage
      const pagingData = this.filteredChartData
        .filter(itm => itm['result'])
        .slice(pageStartIndex, pageStartIndex + rowsPerPage)
      return pagingData
    },
    errorItems () {
      const errors = this.pagingData
        .filter(item => item.error)
        .map(item => ({
          _id: item._id,
          error: item.error,
          created_at: item.created_at
        }))
      return errors
    },
    items () {
      const groupedData = this.prepareData(this.pagingData)
      return objectToArray(groupedData)
    },
    datatableItems () {
      const items = this.items.map(item => {
        const values = [...item.value]
        let groupedByKey
        let groupedValues
        groupedByKey = groupArrayByObjectKey(values, FIELD_ID)
        groupedValues = Object.keys(groupedByKey)
          .reduce((c, key) => {
            try {
              if (Array.isArray(groupedByKey[key])) {
                c = c.concat(groupedByKey[key])
              }
            } catch (e) {
              console.log(e, groupedByKey, groupedValues)
            }
            return c
          }, [])
        return {...item, value: groupedValues}
      }).filter(item => item)
      return sortByString(items, 'key')
    },
    displayItems () {
      return [
        // ...(this.errorItems.length ? [{ key: 'Errors', value: this.errorItems }] : []),
        ...this.datatableItems
      ]
    },
    datatableTotalItems () {
      const { rowsPerPage } = this.datatable.pagination
      const totalItems = this.filteredChartData.filter(item => getProp(item, 'result')).length
      this.datatable.pagination.totalItems = totalItems
      return Math.ceil(totalItems / rowsPerPage)
    }
  },
  watch: {
    'account': {
      // immediate: true,
      handler (val) {
        this.model.account_id = val
      }
    },
    'model.account_id': {
      // immediate: true,
      handler (val) {
        this.$nextTick(() => {
          this.$router.push({ name: 'botMonitoring', query: { account: val } })
          this.refresh()
        })
      }
    },
    'chartFilter': {
      deep: true,
      handler () {
        this.filteredChartLoading = true
        this.$nextTick(() => this.getFilteredChartData())
      }
    }
  },
  async mounted () {
    this.datatable.loading = true
    this.accounts = await this.getAccounts()
    const targetAccount = this.accounts.find(x => x.value === this.account)
    this.model.account_id = (targetAccount && targetAccount.value) || (this.accounts[0] && this.accounts[0].value)
    this.datatable.loading = false
  },
  methods: {
    async getAccounts () {
      // FIXME: fix the datatable errors
      // return [
      //   { key: 1, value: 1 },
      //   { key: 2, value: 2 },
      //   { key: 3, value: 3 }
      // ]
      return (await this.accountService.find({
        sort: { account_id: 1 },
        projection: { account_id: 1 },
        limit: 1000
      })).map(({account_id}) => {
        return { key: account_id, value: account_id }
      })
    },
    async setBotConfig (accountId) {
      try {
        // FIXME: fix the datatable errors
        // FIXME: Create_order is not the samew length like the others
        // let data = testData()
        // data = data.map(item => {
        //   // const key = 'create_order'
        //   Object.keys(item.result).forEach(key => {
        //     if (item.result) {
        //       item.result[key] = [{ x: 1, y: 2 }]
        //     }
        //   })
        //   return item
        // })
        // return data
        this.model.botConfig = (await this.botConfigsService.find({
          'filter': { account_id: accountId },
          'sort': { created_at: -1 },
          'projection': { account_id: 1, type: 1, is_active: 1, simulator: 1 },
          'limit': 1
        }))[0]
        const botType = getProp(this.model.botConfig || {}, 'type')
        if (botType === undefined) {
          return []
        }
        return [{}]
        // return await this.dataService.find({
        //   filter: {
        //     type: `bot-${botType}`,
        //     account_id: accountId
        //   },
        //   sort: {
        //     created_at: -1
        //   },
        //   limit: 500
        // })
      } catch ({ message }) {
        Alert.error({ message })
      }
      return []
    },
    prepareData (data = []) {
      const group = {}
      const itemHeaders = sortByString(
        mergeObjectKeyFromArray(this.data.map(item => item.result).filter(item => item))
      )
      itemHeaders.forEach(key => {
        group[key] = []
      })
      data
        .filter(row => row.result)
        .forEach(row => {
          const id = row[FIELD_ID]
          const createdAt = row['created_at']
          Object.keys(row.result || {})
            .forEach(key => {
              try {
                if (isArray(row.result[key])) {
                  group[key] = (group[key] || []).concat(
                    row.result[key].map(item => {
                      return { 'created_at': createdAt, ...item, ...{'created_at': createdAt}, [FIELD_ID]: id }
                    })
                  )
                } else if (isDefined(row.result[key])) {
                  group[key] = (group[key] || []).concat(
                    [{ 'created_at': createdAt, ...row.result[key], ...{'created_at': createdAt}, [FIELD_ID]: id }]
                  )
                }
              } catch (e) {
                console.log(e, key, row.result, group)
              }
            })
        })
      return group
    },
    async refresh () {
      this.datatable.loading = true
      this.tab = null
      this.$refs.chart && this.$refs.chart.refreshChartToLatest()
      this.data = []
      await this.setBotConfig(this.model.account_id)
      this.botConfigTags = this.getBotConfigTags()
      this.datatable.loading = false
      this.datatable.ready = true
    },
    onClickRow ({ index, item }) {
      if (this.datatable.selectedId === item['created_at']) {
        this.datatable.selectedId = null
      } else {
        this.datatable.selectedId = item['created_at']
      }
    },
    onChartChanged ({ min, max }) {
      this.data = []
      this.getFilteredChartData()
      // this.chartFilter.min = min
      // this.chartFilter.max = max
    },
    onChartClicked ({ error, item, scope, index, groupedData, from, to }) {
      this.$nextTick(async () => {
        if (error) {
          const { requestId, message } = error
          await CloudWatch.showResult({ requestId, message })
        } else {
          this.data = groupedData
          this.datatable.currentGrouping.from = from
          this.datatable.currentGrouping.to = to
          this.getFilteredChartData()
          this.$nextTick(async () => {
            const { rowsPerPage } = this.datatable.pagination
            // const pageItemIndex = findIndex(
            //   this.filteredChartData.filter(item => item['result']),
            //   dItem => dItem[FIELD_ID] === item[FIELD_ID]
            // )
            const pageItem = this.data[index]
            const pageItemIndex = this.filteredChartData.filter(itm => itm['result']).indexOf(pageItem)
            const pageIndex = Math.floor(pageItemIndex / rowsPerPage)
            const tabIndex = findIndex(this.displayItems, itm => itm.key === scope)
            if (tabIndex >= 0) {
              this.tab = `${tabIndex}`
              if (pageIndex >= 0) {
                setTimeout(() => {
                  this.datatable.pagination.page = pageIndex + 1
                }, 500)
              }
            }
            this.datatable.selectedId = item['created_at']
            this.$nextTick(() => {
              setTimeout(() => {
                $('html, body').animate({
                  scrollTop: $(this.$refs.monitorTabs.$el).offset().top - 10
                }, 300)
              }, 500)
            })
          })
        }
      })
    },
    getBotConfigTags () {
      const botConfig = this.model.botConfig
      if (!botConfig) {
        return []
      }
      return Object.keys(botConfig)
        .map(key => isBoolean(botConfig[key]) ? { key: titleText(key), value: botConfig[key] } : undefined)
        .filter(value => value !== undefined)
    }
  },
  components: {
    LogSearch,
    MonitorTable,
    MongoDataTable,
    MongoDataTableFooter,
    BotMonitoringChart
  }
}
</script>
